import React, { useEffect, useMemo, useState } from 'react'
import PopUp from '../../../../ui/PopUp/PopUp'
import Button from '../../../../ui/Button/Button'
import StatusMessage, { useStatusMessage } from '../../../../ui/StatusMessage/StatusMessage'
import sendManualSms from '../../../../api/sms/sendManualSms'
import { createForm } from '../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../utils/newforms/render/getFieldRenderObject'
import FormRender from '../../../../utils/newforms/render/FormRender'
import isFormValid from '../../../../utils/newforms/validation/isFormValid'
import getFormValues from '../../../../utils/newforms/getFormValues'
import {
  MANUAL_SMS_MAX_LENGTH,
  createClientRequestId,
  estimateSmsSegments,
} from './smsUiUtils'

class SmsComposeForm {
  constructor() {
    this.recipient = {
      field: {
        fieldId: 'recipient',
        fieldType: 'phone',
        required: true,
        label: 'Номер телефону',
        defaultCountry: 'UA',
      },
      render: getFieldRenderObject(),
    }
  }
}

const resolveRecipientE164 = (recipient) => {
  if (!recipient) return ''
  if (typeof recipient === 'string') return recipient.trim()
  return String(recipient.metadata?.number || recipient.value || '').trim()
}

function SmsComposePopUp({ show, onClose, onSuccess }) {
  const [form, setForm] = useState(null)
  const [text, setText] = useState('')
  const [reason, setReason] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [step, setStep] = useState('edit') // edit | confirm
  const [isLoading, setIsLoading] = useState(false)
  const [confirmRecipient, setConfirmRecipient] = useState('')
  const [statusMessage, setStatusMessage] = useStatusMessage()

  const segments = useMemo(() => estimateSmsSegments(text), [text])

  useEffect(() => {
    if (!show) return undefined
    setForm(createForm({ formPattern: new SmsComposeForm() }))
    setText('')
    setReason('')
    setShowErrors(false)
    setStep('edit')
    setIsLoading(false)
    setConfirmRecipient('')
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    return undefined
  }, [show, setStatusMessage])

  const handleClose = () => {
    setForm(null)
    setText('')
    setReason('')
    setShowErrors(false)
    setStep('edit')
    setIsLoading(false)
    setConfirmRecipient('')
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    onClose()
  }

  const showFail = (message) => {
    setStatusMessage({ show: true, type: 'fail', message, closeAfter: 7000 })
  }

  const validate = () => {
    if (!form || !isFormValid({ form })) {
      setShowErrors(true)
      return 'Перевірте номер телефону'
    }
    const values = getFormValues({ form })
    const recipientE164 = resolveRecipientE164(values.recipient)
    if (!/^\+380\d{9}$/.test(recipientE164)) {
      setShowErrors(true)
      return 'Тільки українські номери у форматі +380XXXXXXXXX'
    }
    if (!text.trim()) return 'Вкажіть текст SMS'
    if (text.length > MANUAL_SMS_MAX_LENGTH) return `Максимум ${MANUAL_SMS_MAX_LENGTH} символів`
    if (!reason.trim()) return 'Вкажіть причину відправки'
    return ''
  }

  const goConfirm = () => {
    const nextError = validate()
    if (nextError) {
      showFail(nextError)
      return
    }
    const values = getFormValues({ form })
    setConfirmRecipient(resolveRecipientE164(values.recipient))
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    setStep('confirm')
  }

  const submit = async () => {
    setIsLoading(true)
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    const result = await sendManualSms({
      recipientE164: confirmRecipient,
      text: text.trim(),
      reason: reason.trim(),
      clientRequestId: createClientRequestId(),
    })
    setIsLoading(false)
    if (!result.ok) {
      showFail(result.message || 'Не вдалося надіслати SMS')
      setStep('edit')
      return
    }
    if (result.data && result.data.ok === false && result.data.outcome === 'failed') {
      showFail(result.data.normalizedError || result.data.errorCode || 'Відправку відхилено')
      setStep('edit')
      return
    }
    handleClose()
    onSuccess?.(result.data)
  }

  return (
    <PopUp show={show} title="Нове SMS" close={handleClose}>
      <div className="SmsCompose">
        {statusMessage.show ? (
          <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
        ) : null}
        {step === 'edit' ? (
          <>
            <div className="SmsCompose-Phone">
              <FormRender
                sections={[{ fields: ['recipient'] }]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
            </div>
            <label className="SmsCompose-Field">
              <span>Текст</span>
              <textarea
                rows={5}
                value={text}
                maxLength={MANUAL_SMS_MAX_LENGTH}
                onChange={(event) => setText(event.target.value)}
              />
              <span className="SmsCompose-Meta">
                {segments.length}/{MANUAL_SMS_MAX_LENGTH} · ~{segments.segments} сегм.
              </span>
            </label>
            <label className="SmsCompose-Field">
              <span>Причина</span>
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Навіщо надсилаєте це SMS"
              />
            </label>
            <div className="SmsCompose-Actions">
              <Button title="Скасувати" theme="solid" fill="normal" size={32} onClick={handleClose} />
              <Button title="Далі" theme="solid" fill="accent" size={32} onClick={goConfirm} />
            </div>
          </>
        ) : (
          <>
            <p className="SmsCompose-PreviewTitle">Підтвердіть відправку</p>
            <p>
              <strong>Кому:</strong> {confirmRecipient}
            </p>
            <p>
              <strong>Причина:</strong> {reason}
            </p>
            <pre className="SmsCompose-PreviewText">{text}</pre>
            <div className="SmsCompose-Actions">
              <Button title="Назад" theme="solid" fill="normal" size={32} onClick={() => setStep('edit')} />
              <Button
                title="Надіслати"
                theme="solid"
                fill="accent"
                size={32}
                isLoading={isLoading}
                onClick={submit}
              />
            </div>
          </>
        )}
      </div>
    </PopUp>
  )
}

export default SmsComposePopUp
